<template>
  <div class="edition-table">
    <table>
      <thead>
        <th>Quartos</th>
        <th class="edition-table__close">Fechar</th>
        <th class="edition-table__cta">CTA</th>
        <th class="edition-table__ctd">CTD</th>
        <th class="edition-table__discount">Desconto (%)</th>

        <!-- HÓSPEDES -->
        <th
          v-for="(pax, i) in paxOptionsStrings"
          :key="`th-pax-${i}`"
          class="edition-table__value"
        >
          {{ pax }}
        </th>
        <!-- / HÓSPEDES -->
        <th class="edition-table__disp-lestbook">
          Disp. Let’s Book
        </th>
        <th>
          Prioridade
          <InfoTooltip
            :text="infoText"
            :tooltipMinWidth="300"
            class="tooltip-space"
          />
        </th>
        <th class="edition-table__restriction">Restrição mín. de noites</th>
      </thead>
      <tbody>
        <!-- LINHA -->
        <tr v-for="(room, i) in rooms" :key="`room-${room.codigoQuarto || i}`">
          <!-- QUARTOS -->
          <td class="td__padd">{{ room.quarto }}</td>

          <!-- FECHAR -->
          <td class="td__padd">
            <InputCheckbox v-model="room.fechado" :checked="room.fechado" />
          </td>

          <!-- CTA -->
          <td class="td__padd">
            <InputCheckbox v-model="room.cta" :checked="room.cta" />
          </td>

          <!-- CTD -->
          <td class="td__padd">
            <InputCheckbox v-model="room.ctd" :checked="room.ctd" />
          </td>

          <!-- DESCONTO -->
          <td>
            <TableInputCell
              v-model="room.desconto"
              :id="`${room.quarto}-desconto`"
              :currency="true"
              :min="0"
              :max="99"
              :prefix="'-'"
              :sufix="'%'"
              type="number"
              placeholder=""
            />
          </td>

          <!-- HÓSPEDES -->
          <td
            v-for="(pax, i) in getPossiblePaxColumns(room)"
            :key="`td-pax-${i}`"
            :class="{
              empty: !pax
            }"
            class="td__padless"
          >
            <TableInputCell
              v-if="pax"
              v-model="pax.valor"
              :id="`${room.quarto}-guest-value-${i}`"
              :prefix="getCurrencySymbol"
              type="number"
              placeholder="0,00"
              currency
            />
          </td>
          <!-- / HÓSPEDES -->

          <!-- DISP. LET’S BOOK -->
          <td>
            <TableInputCell
              v-if="room.quantidadeDisponivel || room.quantidadeDisponivel > -1"
              v-model="room.quantidadeDisponivel"
              :id="`${room.quarto}-disponibility`"
              type="number"
            />
          </td>

          <!-- PRIORIDADE -->
          <td class="td__padd">
            <InputCheckbox
              v-model="room.prioridadeLB"
              :checked="room.prioridadeLB"
            />
          </td>

          <!-- RESTRIÇÃO MÍN. DE NOITES -->
          <td class="rest-td">
            <b-row no-gutters>
              <b-col class="td">
                <TableSelectCell
                  :options="minNightsOptions"
                  :value="
                    minNightsOptions.find(
                      (option) => +option.value === +room.tipoMinimoNoites
                    )
                  "
                  @change="room.tipoMinimoNoites = $event.value"
                />
              </b-col>
              <b-col
                class="td d-flex align-items-center td__min-width"
                cols="3"
              >
                <TableInputCell
                  v-if="room.minimoNoites || room.minimoNoites > -1"
                  v-model="room.minimoNoites"
                  :id="`${room.quarto}-nightsNumber`"
              /></b-col>
            </b-row>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
/** ======================================================= */
/** Componente da tabela de visualização da tarifa absoluta */
/** ======================================================= */
import { mapGetters } from 'vuex'
import InputCheckbox from '@/components/_base/elements/InputCheckbox'
import InfoTooltip from '@/components/_base/patterns/InfoTooltip'
import TableInputCell from './TableInputCell'
import TableSelectCell from './TableSelectCell'

export default {
  name: 'AbsoluteTable',
  components: {
    InputCheckbox,
    InfoTooltip,
    TableInputCell,
    TableSelectCell
  },
  props: {
    /** Configuração dos quartos */
    rooms: {
      type: Array,
      default: () => []
    },
    /** Pegar data */
    date: {
      type: [String, Date],
      default: null
    }
  },
  data() {
    return {
      minNightsOptions: [
        { text: 'Selecione', value: null },
        { text: 'Chegando em', value: 1 },
        { text: 'Passando por', value: 2 }
      ],
      infoText:
        'Ao marcar como prioridade, as disponibilidades e restrições ignoram qualquer dado que vier de integração.'
    }
  },
  computed: {
    /**
     * Retorna a possibilidade máxima de adultos e crianças
     * entre todos os quartos
     */
    paxOptions() {
      return (
        (this.rooms || [])
          .flatMap((item) => item.valoresPax)
          /** Filtra apenas valores que não são repetidos */
          .filter(
            (item, index, arr) =>
              index ===
              arr.findIndex(
                (elm) =>
                  elm.quantidadePax === item.quantidadePax &&
                  elm.tipoPax === item.tipoPax
              )
          )
          /** Ordena em ordem crescente adultos seguido de crianças */
          .sort((a, b) => {
            if (a.tipoPax > b.tipoPax) return -1
            if (b.tipoPax > a.tipoPax) return 1

            return 0
          })
      )
    },
    /** Retorna os textos dos cabeçalhos */
    paxOptionsStrings() {
      return this.paxOptions.map((item) => {
        if (item.tipoPax === 8)
          return `${this.$options.filters.pluralizer(
            item.quantidadePax,
            'Criança | Crianças'
          )}`

        return `${item.quantidadePax} Pax`
      })
    },
    ...mapGetters('hotel', ['getCurrency', 'getCurrencySymbol'])
  },
  methods: {
    /**
     * Retorna um array com os campos de hóspedes e vazios de cada quarto
     *
     * @param {Object} room - Objeto do quarto a ser pesquisado
     */
    getPossiblePaxColumns(room) {
      /** Cria um novo array preenchido com null */
      const cols = new Array(this.paxOptions.length).fill(null)

      room.valoresPax.forEach((roomPax) => {
        const idx = this.paxOptions.findIndex(
          (paxOptionsItem) =>
            paxOptionsItem.tipoPax === roomPax.tipoPax &&
            paxOptionsItem.quantidadePax === roomPax.quantidadePax
        )

        if (idx > -1) {
          cols[idx] = roomPax
        }
      })

      return cols
    }
  }
}
</script>

<style lang="scss" scoped>
$cell-padding: 12px 15px;

.tooltip-space {
  margin-left: 7px;
}

.edition-table {
  max-width: 1236px;
  overflow-x: auto;

  &__close {
    width: 77px;
    min-width: 77px;
  }

  &__cta {
    width: 77px;
    min-width: 77px;
  }

  &__ctd {
    width: 77px;
    min-width: 77px;
  }

  &__discount {
    width: 110px;
    min-width: 110px;
  }

  &__value {
    width: 80px;
    min-width: 80px;
  }

  &__disp-lestbook {
    width: 120px;
    min-width: 120px;
  }

  &__restriction {
    width: 170px;
    min-width: 170px;
  }

  table {
    th {
      padding: $cell-padding;
    }

    th,
    td {
      vertical-align: middle;
      border: 1px solid $gray-border;
      font-size: font-size('size-small');
      white-space: nowrap;

      & + & {
        border-left: none;
      }

      &.empty {
        background-color: $gray-lightest;
      }
    }

    th {
      font-weight: font-weight('weight-medium');
    }

    .td {
      padding: 0;
      min-width: 1px;

      &__padd {
        padding: $cell-padding;
        ::v-deep .input-checkbox {
          margin: 0px auto !important;
          display: table;
        }
      }
    }

    .rest-td {
      .td {
        &:first-child {
          border-right: 1px solid $gray-border;
        }

        &:last-child {
          ::v-deep {
            .content-wrapper {
              padding: {
                left: 10px;
                right: 10px;
              }
            }
          }
        }
      }
    }
  }
}
</style>
